"use client";

/**
 * useImageBannerPaging
 * @description 넘김 배너의 현재 페이지를 추적하고 앞뒤로 넘기고, 정해진 간격으로 스스로 넘긴다.
 *   폭은 상자에서 그때그때 읽는다 — 회전·창 크기 변경에서 한 번 잰 값은 곧 틀린 값이 된다.
 *
 *   현재 페이지는 스크롤 위치를 페이지 폭으로 나눠 반올림한 값이다 — 절반 넘게 보이는
 *   페이지를 현재로 친다(앱의 50% viewability 와 같은 규칙).
 *
 *   자동 넘김은 **손이 닿아 있으면 멈춘다** — 마우스가 올라와 있거나 안에 포커스가 있거나
 *   탭이 가려졌을 때. 움직임을 줄인 설정에서는 아예 돌지 않는다.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

export function useImageBannerPaging(
  count: number,
  autoplayMs = 0,
): {
  trackRef: RefObject<HTMLDivElement | null>;
  activeIndex: number;
  onScroll: () => void;
  goTo: (index: number) => void;
  pause: () => void;
  resume: () => void;
} {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    setActiveIndex(Math.round(track.scrollLeft / track.clientWidth));
  }, []);

  const goTo = useCallback(
    (index: number) => {
      const track = trackRef.current;
      if (!track || count === 0) return;
      // 양 끝에서는 반대편으로 돈다 — 마지막 장에서 "다음" 이 죽어 있으면 끝난 줄 안다.
      const target = (index + count) % count;
      track.scrollTo({ left: track.clientWidth * target, behavior: "smooth" });
    },
    [count],
  );

  const pause = useCallback(() => setPaused(true), []);
  const resume = useCallback(() => setPaused(false), []);

  useEffect(() => {
    if (!autoplayMs || count < 2 || paused) return;
    if (window.matchMedia(REDUCED_MOTION).matches) return;

    const timer = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      goTo(activeIndex + 1);
    }, autoplayMs);
    return () => window.clearInterval(timer);
  }, [activeIndex, autoplayMs, count, goTo, paused]);

  return { trackRef, activeIndex, onScroll, goTo, pause, resume };
}
